import React, { useMemo, useState } from "react";
import jwt from "jwt-decode";
import { IAuthContextObj, TLoginHandler, TSSOHandler } from "../types/index";

const AuthContext = React.createContext<IAuthContextObj>({
  token: "",
  isLoggedIn: false,
  login: () => null,
  logout: () => null,
  name: "",
  scopes: [],
  hexMode: false,
  bulkLimit: "1000",
  authenticateWithToken: () => false,
});

const getScopes = (scopes: string) => {
  return scopes.split(",").reduce((acc, el) => {
    const [scope, page] = el.split(":");
    return {
      ...acc,
      [page]: scope.split(""),
    };
  }, {});
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const storedToken = localStorage.getItem("token");
  const storedName = localStorage.getItem("name");
  const storedScopes = localStorage.getItem("scopes");
  const storedHexMode = localStorage.getItem("hexMode");
  const storedBulkLimit = localStorage.getItem("bulkLimit");

  const [token, setToken] = useState(storedToken);
  const [name, setName] = useState(storedName);
  const [scopes, setScopes] = useState(storedScopes);
  const [hexMode, setHexMode] = useState(storedHexMode === "yes");
  const [bulkLimit, setBulkLimit] = useState(storedBulkLimit);

  const logoutHandler = () => {
    setToken(null);
    setName(null);
    setScopes(null);
    setHexMode(false);
    setBulkLimit(null);

    // Check if running in iframe - in SSO we are not storing token in localstorage
    const isInIframe = window !== window.parent;
    if (isInIframe) {
      window.location.href = "/login";
      window.parent.postMessage(
        { type: "LOGOUT" },
        `${process.env.REACT_APP_PARENT_URL}`
      );
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("name");
      localStorage.removeItem("scopes");
      localStorage.removeItem("hexMode");
      localStorage.removeItem("bulkLimit");
    }
  };

  // Function to authenticate with token (used by SSO)
  const authenticateWithTokenHandler: TSSOHandler = (token, hex, bulkLimit) => {
    try {
      if (token) {
        setToken(token);
        const data: { name: string; scopes: string } = jwt(token);
        setName(data.name);
        setScopes(data.scopes);
        setHexMode(hex === "yes");
        setBulkLimit(bulkLimit);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Token authentication failed:", error);
      return false;
    }
  };

  const loginHandler: TLoginHandler = (token, hex, bulkLimit) => {
    if (token) {
      const data: { name: string; scopes: string } = jwt(token);

      setToken(token);
      setName(data.name);
      setScopes(data.scopes);
      setHexMode(hex === "yes");
      setBulkLimit(bulkLimit);
      localStorage.setItem("token", token);
      localStorage.setItem("name", data.name);
      localStorage.setItem("scopes", data.scopes);
      localStorage.setItem("hexMode", hex);
      localStorage.setItem("bulkLimit", bulkLimit);
    }
  };

  const authValue = useMemo(
    () => ({
      token,
      isLoggedIn: !!token,
      login: loginHandler,
      logout: logoutHandler,
      name,
      scopes: scopes ? getScopes(scopes) : null,
      hexMode,
      bulkLimit,
      authenticateWithToken: authenticateWithTokenHandler,
    }),
    [token, name, scopes, hexMode, bulkLimit]
  );

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
